import { ArrowRight, Loader2, X } from "lucide-react";
import { motion, useReducedMotion } from "motion/react";
import { useState, type ReactNode } from "react";

/**
 * The "Mash is invite only" gate, in the editorial style: flat white, oversized
 * left-aligned display type, a rule-only field and one violet pill CTA. Colour
 * is rationed to the brand violet — the word "invite", the CTA, and whatever
 * the footer renders.
 *
 * Shared by the two places a code is demanded, so they can't drift apart:
 *
 * - `/signup` (`SignupInviteMobile`) — the pre-account gate.
 * - `InviteLinkPrompt` — a signed-in user whose invite isn't accepted yet, who
 *   stays blocked out of the app until they clear it.
 *
 * `onSubmit` owns what a code *means*; rejecting with an `Error` surfaces its
 * message under the field.
 */
export function InviteGate({
  subtitle,
  onSubmit,
  onDismiss,
  dismissLabel = "Close",
  footer,
  greeting,
}: {
  subtitle: ReactNode;
  /** Validate/act on the code. Reject with an Error to show its message. */
  onSubmit: (code: string) => Promise<void> | void;
  /** Top-left dismiss. Omitted when there is nowhere to go. */
  onDismiss?: () => void;
  dismissLabel?: string;
  /** Bottom row — a way out, or a way to switch accounts. */
  footer?: ReactNode;
  /** Small line above the headline, e.g. "Welcome, Ada". */
  greeting?: string;
}) {
  const reduceMotion = useReducedMotion();
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!code.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await onSubmit(code);
    } catch (err) {
      setError(
        err instanceof Error && err.message
          ? err.message
          : "That invite code is invalid or expired.",
      );
    } finally {
      setBusy(false);
    }
  };

  /** Staggered fade-up; skipped under reduced motion. */
  const enter = (delay: number) =>
    reduceMotion
      ? {}
      : {
          initial: { opacity: 0, y: 18 },
          animate: { opacity: 1, y: 0 },
          transition: {
            duration: 0.5,
            ease: [0.22, 1, 0.36, 1] as const,
            delay,
          },
        };

  const disabled = !code.trim() || busy;

  return (
    <div className="text-sl-950 flex min-h-screen w-full flex-col bg-white">
      <div className="flex px-7 pt-[max(3rem,calc(env(safe-area-inset-top)+0.75rem))]">
        {onDismiss ? (
          <button
            type="button"
            onClick={onDismiss}
            aria-label={dismissLabel}
            className="text-sl-500 hover:bg-sl-100 hover:text-sl-950 -ml-2 inline-flex size-10 items-center justify-center rounded-full transition"
          >
            <X className="h-5 w-5" strokeWidth={1.75} />
          </button>
        ) : (
          <span className="size-10" aria-hidden="true" />
        )}
      </div>

      <div className="flex flex-1 flex-col justify-center px-7 pb-6">
        {greeting ? (
          <motion.p
            {...enter(0)}
            className="text-sl-500 mb-3 text-[15px] leading-6"
          >
            {greeting}
          </motion.p>
        ) : null}

        <motion.h1
          {...enter(greeting ? 0.04 : 0)}
          className="text-[clamp(2.75rem,15vw,3.5rem)] leading-[0.95] font-extrabold tracking-[-0.045em]"
        >
          {/* Explicit trailing spaces: <br> contributes no whitespace to the
              accessible name, so without them it reads "Mash isinviteonly." */}
          {"Mash is "}
          <br />
          <span className="text-primary">invite</span> <br />
          {"only."}
        </motion.h1>

        <motion.p
          {...enter(0.06)}
          className="text-sl-500 mt-6 max-w-72 text-[15px] leading-6"
        >
          {subtitle}
        </motion.p>

        <motion.form
          {...enter(0.12)}
          onSubmit={handleSubmit}
          className="mt-10 flex flex-col"
        >
          <div className="relative">
            <input
              type="text"
              value={code}
              onChange={e => {
                setCode(e.target.value);
                setError(null);
              }}
              placeholder="invite code or link"
              autoCapitalize="none"
              autoCorrect="off"
              autoComplete="one-time-code"
              spellCheck={false}
              enterKeyHint="go"
              aria-label="Invite code"
              aria-invalid={Boolean(error)}
              aria-describedby={error ? "invite-code-error" : undefined}
              className={`caret-primary placeholder:text-sl-300 w-full border-0 border-b bg-transparent pr-10 pb-3.5 text-[20px] font-medium tracking-[-0.01em] transition-colors outline-none placeholder:font-normal ${
                error
                  ? "border-b-re-200"
                  : "border-b-sl-200 focus:border-b-primary"
              }`}
            />
            {code ? (
              <button
                type="button"
                onClick={() => {
                  setCode("");
                  setError(null);
                }}
                aria-label="Clear invite code"
                className="text-sl-300 hover:bg-ev-100 hover:text-primary absolute right-0 bottom-3 inline-flex size-8 items-center justify-center rounded-full transition"
              >
                <X className="h-4 w-4" strokeWidth={2} />
              </button>
            ) : null}
          </div>

          <div aria-live="polite" className="min-h-6 pt-3">
            {error ? (
              <p
                id="invite-code-error"
                className="text-re-200 text-[13px] leading-5"
              >
                {error}
              </p>
            ) : null}
          </div>

          <button
            type="submit"
            disabled={disabled}
            className={`mt-6 flex h-15 w-full items-center justify-between rounded-full pr-3 pl-7 text-[16px] font-semibold transition ${
              disabled
                ? "bg-sl-100 text-sl-300 cursor-not-allowed"
                : "bg-primary text-white active:scale-[0.99]"
            }`}
          >
            Continue
            <span
              aria-hidden="true"
              className={`inline-flex size-11 items-center justify-center rounded-full transition ${
                disabled ? "bg-transparent" : "bg-white/20"
              }`}
            >
              {busy ? (
                <Loader2 className="h-4.5 w-4.5 animate-spin" />
              ) : (
                <ArrowRight className="h-4.5 w-4.5" strokeWidth={2.25} />
              )}
            </span>
          </button>
        </motion.form>
      </div>

      {footer ? (
        <motion.div
          {...enter(0.18)}
          className="flex justify-end px-7 pb-[max(2rem,env(safe-area-inset-bottom))]"
        >
          {footer}
        </motion.div>
      ) : null}
    </div>
  );
}
